import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ImagePlus, Loader2, X } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useFileUpload } from "@/hooks/useFileUpload";

export interface SingleImageUploadProps {
  value: string | null;
  onChange: (url: string | null) => void;
  onRemove?: () => void;
  disabled?: boolean;
  className?: string;
  initialImageUrl?: string;
}

export function SingleImageUpload({
  value,
  onChange,
  onRemove,
  disabled,
  className,
  initialImageUrl
}: SingleImageUploadProps) {
  const { uploadFile, isUploading } = useFileUpload();
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    const imageUrl = value || initialImageUrl;
    if (imageUrl) {
      console.log("Setting preview from URL:", imageUrl.substring(0, 30) + '...');
      setPreview(imageUrl);
      // Reset error when a new URL is provided
      setError(null);
    } else {
      console.log("No image URL provided for preview");
      setPreview(null);
    }
  }, [value, initialImageUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      setIsImageLoading(true);
      console.log('Starting image upload process:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: `${(file.size / 1024).toFixed(2)} KB`
      });
      
      // Before upload, verify it's an image file
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }
      
      const url = await uploadFile(file);
      if (url) {
        console.log('Image upload successful, received URL:', url.substring(0, 30) + '...');
        
        // Create a new image object to preload the image
        const img = new Image();
        
        // Handle image loading success
        img.onload = () => {
          console.log('Image loaded successfully');
          setPreview(url);
          onChange(url);
          setIsImageLoading(false);
          toast.success('Image uploaded successfully');
        };
        
        // Handle image loading failure
        img.onerror = () => {
          console.error('Failed to load image from URL');
          setError("Failed to load uploaded image. Please try again.");
          setIsImageLoading(false);
          toast.error("Failed to load image");
        };
        
        // Set a timeout in case the image takes too long to load
        const timeoutId = setTimeout(() => {
          if (isImageLoading) {
            console.warn('Image loading timed out, but continuing with URL');
            setPreview(url);
            onChange(url);
            setIsImageLoading(false);
          }
        }, 5000);
        
        // Set image source to trigger load
        img.src = url;
        
        // Clean up timeout if component unmounts
        return () => clearTimeout(timeoutId);
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to upload image";
      setError(errorMessage === 'undefined' ? "Failed to upload image" : errorMessage);
      toast.error(errorMessage === 'undefined' ? "Failed to upload image" : errorMessage);
      setIsImageLoading(false);
    } finally {
      // Clear the input
      e.target.value = '';
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    onChange(null);
    onRemove?.();
    toast.success('Image removed');
  };

  const handleImageError = () => {
    console.error('Failed to load image:', preview);
    setError("Failed to load image");
    // Don't clear the preview or value here, as it might be a temporary loading issue
  };

  return (
    <div className={cn("space-y-2", className)}>
      {preview ? (
        <div className="relative aspect-square rounded-lg border overflow-hidden">
          <img
            src={preview}
            alt="Uploaded image"
            className={cn(
              "h-full w-full object-cover",
              isImageLoading && "opacity-50"
            )}
            onError={handleImageError}
          />
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/10">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 bg-black/40 transition-opacity hover:opacity-100">
            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={handleRemove}
              disabled={disabled || isImageLoading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <label
          className={cn(
            "flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer",
            "hover:border-primary/50 transition-colors",
            disabled && "opacity-50 cursor-not-allowed",
            error && "border-destructive"
          )}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            {isUploading || isImageLoading ? (
              <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
            ) : (
              <ImagePlus className="h-8 w-8 text-muted-foreground mb-2" />
            )}
            <p className="text-sm text-muted-foreground">
              {isUploading ? "Uploading..." : isImageLoading ? "Loading..." : "Click to upload image"}
            </p>
          </div>
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
            disabled={disabled || isUploading || isImageLoading}
          />
        </label>
      )}
      {error && error !== 'undefined' && (
        <p className="text-sm text-destructive mt-1">{error}</p>
      )}
    </div>
  );
} 